import { Mail, Phone, Building2, MapPin, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { useCustomerComplaints } from "@/hooks/useCustomers";
import { Customer } from "@/api/customer.api";
import { format } from "date-fns";

interface CustomerDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: Customer | null;
}

export function CustomerDetailDialog({ open, onOpenChange, customer }: CustomerDetailDialogProps) {
  const { data, isLoading } = useCustomerComplaints(customer?.id, 1, 10);

  if (!customer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{customer.fullName}</DialogTitle>
          <DialogDescription className="font-data">{customer.customerCode}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 rounded-md border border-border bg-muted/30 p-4 text-sm">
          <div className="flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-data">{customer.phone}</span>
          </div>
          {customer.email && (
            <div className="flex items-center gap-2">
              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{customer.email}</span>
            </div>
          )}
          {customer.company && (
            <div className="flex items-center gap-2">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{customer.company}</span>
            </div>
          )}
          {customer.address && (
            <div className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{customer.address}</span>
            </div>
          )}
          {customer.notes && (
            <div className="col-span-2 flex items-start gap-2 border-t border-border pt-3">
              <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="text-muted-foreground">{customer.notes}</span>
            </div>
          )}
        </div>

        <div>
          <h4 className="mb-2 font-display text-sm font-semibold">Complaint history</h4>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : !data || data.items.length === 0 ? (
            <p className="rounded-md border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
              No complaints logged for this customer yet.
            </p>
          ) : (
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {data.items.map((c) => (
                <div
                  key={c.id}
                  className="service-tag flex items-center justify-between border border-border bg-card px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium font-data">{c.complaintNumber}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {c.productName} · {format(new Date(c.createdAt), "dd MMM yyyy")}
                      {c.assignedEmployee ? ` · ${c.assignedEmployee.fullName}` : ""}
                    </p>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
